(function(){
  // ====== CONFIG ======
  const TIMEOUT_MS = 2500;
  const SUGGEST_LIMIT = 10;
  const SCORE_THRESHOLD = 80; // monte à 88-90 pour plus strict

  // Produit (model code)
  const PRODUCT_SUGGEST_LIMIT = 5;   // nb produits suggérés à récupérer
  const PRODUCT_CHECK_LIMIT = 3;     // nb max de /products/<handle>.js à vérifier
  const MODEL_CODE_MIN_LEN = 5;

  // Si tes codes sont toujours numériques (ex: 8828517), mets true et ajuste la regex
  const MODEL_CODE_NUMERIC_ONLY = true;

  // ====== Utils fetch ======
  async function fetchWithTimeout(url){
    const ctrl = new AbortController();
    const tm = setTimeout(()=>ctrl.abort(), TIMEOUT_MS);
    try{
      return await fetch(url, { signal: ctrl.signal });
    } finally {
      clearTimeout(tm);
    }
  }

  // ====== Utils texte ======
  function simplify(s){
    return String(s||'')
      .normalize('NFD').replace(/[\u0300-\u036f]/g,'') // accents
      .replace(/['’]/g,' ')                             // apostrophes
      .replace(/[^a-z0-9]+/gi,' ')                      // non alnum -> espace
      .toLowerCase().trim().replace(/\s+/g,' ');
  }
  function toHandleLike(s){ return simplify(s).replace(/\s+/g,'-'); }

  function degeminate(s){
    return String(s||'').replace(/([a-z])\1+/gi,'$1');
  }

  function normalizeCode(s){
    return String(s||'').toLowerCase().replace(/[^a-z0-9]/g,'');
  }

  // Détecte si la requête ressemble à un model code
  function looksLikeModelCode(raw){
    const q = String(raw||'').trim();
    if (q.length < MODEL_CODE_MIN_LEN) return false;

    if (MODEL_CODE_NUMERIC_ONLY){
      // ajuste selon tes refs (ex: 6-10 chiffres)
      return /^\d{6,10}$/.test(q);
    }

    // alphanum sans espaces
    return /^[a-z0-9][a-z0-9\-_.\/]{4,}$/i.test(q) && !/\s/.test(q);
  }

  // Check si un produit possède une variante dont une option matche le model code.
  // Indépendant de l'ordre ET du nom des options : le backend peut réordonner
  // "Model Code" (option1/2/3) sans casser le matching. La requête est déjà filtrée
  // en amont (looksLikeModelCode → 6-10 chiffres), donc balayer toutes les options
  // ne peut pas matcher une taille/couleur par erreur.
  function productMatchesModelCode(productJson, rawCode){
    const target = normalizeCode(rawCode);
    if (!target) return false;

    for (const v of (productJson?.variants || [])){
      if (normalizeCode(v?.option1) === target) return true;
      if (normalizeCode(v?.option2) === target) return true;
      if (normalizeCode(v?.option3) === target) return true;
      if (Array.isArray(v?.options) && v.options.some(o => normalizeCode(o) === target)) return true;
    }

    return false;
  }

  // Trouve une URL produit (via suggest + vérification /products/<handle>.js)
  async function findProductUrlByModelCode(raw){
    const suggestUrl =
      `/search/suggest.json?q=${encodeURIComponent(raw)}&resources[type]=product&resources[limit]=${PRODUCT_SUGGEST_LIMIT}`;

    let products = [];
    try{
      const r = await fetchWithTimeout(suggestUrl);
      if (!r.ok) return null;
      const data = await r.json();
      products = (data?.resources?.results?.products || [])
        .map(p => ({ title: p.title, handle: p.handle, url: p.url }))
        .filter(p => p?.handle && p?.url);
    }catch(e){
      return null;
    }
    if (!products.length) return null;

    // Vérif “hard” via /products/<handle>.js sur les premiers résultats
    const toCheck = products.slice(0, PRODUCT_CHECK_LIMIT);

    for (const p of toCheck){
      try{
        const r = await fetchWithTimeout(`/products/${encodeURIComponent(p.handle)}.js`);
        if (!r.ok) continue;
        const productJson = await r.json();
        if (productMatchesModelCode(productJson, raw)){
          return p.url; // URL relative Shopify
        }
      }catch(e){
        // ignore
      }
    }
    return null;
  }

  // ====== règles FR (sing/plur) ======
  const EXCEPT_OU = new Set(['bijou','caillou','genou','hibou','joujou','pou']);
  const EXCEPT_AL_AUX_KEEP_AL = new Set(['bal','carnaval','festival','recital','chacal','regal','banal','fatal','natal','naval']);

  function toSingular(w){
    if (w.endsWith('eaux')) return w.slice(0,-1);
    if (w.endsWith('aux')) { const cand = w.slice(0,-3)+'al'; if (!EXCEPT_AL_AUX_KEEP_AL.has(cand)) return cand; }
    if (w.endsWith('x')) { const root = w.slice(0,-1)+'u'; if (EXCEPT_OU.has(root)) return root; }
    if (/[sxz]$/.test(w)) return w.replace(/[sxz]$/,'');
    return w;
  }
  function toPlural(w){
    if (w.endsWith('eau')) return w+'x';
    if (w.endsWith('al') && !EXCEPT_AL_AUX_KEEP_AL.has(w)) return w.slice(0,-2)+'aux';
    if (w.endsWith('ou')) return EXCEPT_OU.has(w) ? w+'x' : w+'s';
    if (/[sxz]$/.test(w)) return w;
    return w+'s';
  }
  function variants(phrase){
    const s = simplify(phrase);
    if (!s) return [];
    const out = new Set([s]);
    const words = s.split(' ');

    out.add(words.map(toSingular).join(' '));
    out.add(words.map(toPlural).join(' '));

    if (words.length>1){
      out.add(words.slice(0,-1).concat(toSingular(words.at(-1))).join(' '));
      out.add(words.slice(0,-1).concat(toPlural(words.at(-1))).join(' '));
      out.add([toSingular(words[0])].concat(words.slice(1)).join(' '));
      out.add([toPlural(words[0])].concat(words.slice(1)).join(' '));
    }

    Array.from(out).forEach(v => out.add(v.replace(/\s+/g,'-')));
    Array.from(out).forEach(v => out.add(degeminate(v)));

    return Array.from(out);
  }

  // ====== Jaro-Winkler (fuzzy rapide) ======
  function jw(a,b){
    if (a===b) return 1;
    const maxDist = Math.floor(Math.max(a.length,b.length)/2)-1;
    let mA = new Array(a.length).fill(false);
    let mB = new Array(b.length).fill(false);
    let matches=0, trans=0;

    for (let i=0;i<a.length;i++){
      const start = Math.max(0, i - maxDist);
      const end   = Math.min(i + maxDist + 1, b.length);
      for (let j=start;j<end;j++){
        if (mB[j]) continue;
        if (a[i] === b[j]) { mA[i]=mB[j]=true; matches++; break; }
      }
    }
    if (!matches) return 0;

    let k=0;
    for (let i=0;i<a.length;i++){
      if (!mA[i]) continue;
      while (!mB[k]) k++;
      if (a[i] !== b[k]) trans++;
      k++;
    }
    const m = matches;
    let jaro = (m/a.length + m/b.length + (m - trans/2)/m)/3;

    let l = 0;
    const maxPrefix = Math.min(4, Math.min(a.length, b.length));
    while (l<maxPrefix && a[l]===b[l]) l++;
    return jaro + l*0.1*(1-jaro);
  }

  // ====== Damerau-Levenshtein borné (≤2) ======
  function dlBounded(a,b,limit=2){
    if (a===b) return 0;
    const la=a.length, lb=b.length;
    if (Math.abs(la-lb)>limit) return limit+1;

    const INF = la+lb;
    const d = [];
    for (let i=0;i<=la;i++){
      d[i] = new Array(lb+1);
      d[i][0] = i;
    }
    for (let j=0;j<=lb;j++){
      d[0][j] = j;
    }

    for (let i=1;i<=la;i++){
      let rowMin = INF;
      for (let j=1;j<=lb;j++){
        const cost = (a[i-1]===b[j-1]) ? 0 : 1;
        const del = d[i-1][j] + 1;
        const ins = d[i][j-1] + 1;
        const sub = d[i-1][j-1] + cost;
        let val = Math.min(del, ins, sub);

        if (i>1 && j>1 && a[i-1]===b[j-2] && a[i-2]===b[j-1]){
          val = Math.min(val, d[i-2][j-2] + 1);
        }
        d[i][j] = val;
        if (val < rowMin) rowMin = val;
      }
      if (rowMin > limit) return limit+1;
    }
    return d[la][lb];
  }

  // ====== Scoring requête ↔ collection ======
  function scoreQueryToCollection(qNorm, col){
    const titleNorm  = simplify(col.title||'');
    const handleNorm = simplify(String(col.handle||'').replace(/-/g,' '));

    const bag = new Set([
      titleNorm,
      handleNorm,
      toHandleLike(col.title||''),
      String(col.handle||'').toLowerCase(),
      degeminate(titleNorm),
      degeminate(handleNorm),
      degeminate(toHandleLike(col.title||'')),
      degeminate(String(col.handle||'').toLowerCase())
    ]);

    const qHandleish = qNorm.replace(/\s+/g,'-');
    if (bag.has(qNorm) || bag.has(qHandleish) || bag.has(degeminate(qNorm)) || bag.has(degeminate(qHandleish)))
      return 100;

    const stop = new Set(['de','du','des','la','le','les','et','pour','un','une','au','aux','en','sur','avec','à','a']);
    const qTokens = qNorm.split(' ').filter(t=>t && !stop.has(t));
    const tTokens = titleNorm.split(' ').filter(t=>t && !stop.has(t));
    const overlap = qTokens.filter(t => tTokens.includes(t)).length;
    const overlapPct = tTokens.length ? Math.round(100*overlap/Math.max(tTokens.length,qTokens.length)) : 0;

    const starts   = [...bag].some(x => x.startsWith(qNorm) || x.startsWith(degeminate(qNorm)));
    const contains = [...bag].some(x => x.includes(qNorm) || x.includes(degeminate(qNorm)));

    const qC = qNorm.replace(/\s+/g,'');
    const cC = titleNorm.replace(/\s+/g,'');
    const fuzzy = Math.round(jw(qC, cC)*100);

    const dl = dlBounded(qC, cC, 2);
    const qCd = degeminate(qC), cCd = degeminate(cC);
    const dlDeg = dlBounded(qCd, cCd, 2);

    let s = 0;
    s += overlapPct * 0.55;
    s += (starts? 20:0) + (contains? 8:0);
    s += Math.min(fuzzy, 85) * 0.28;

    const bestDl = Math.min(dl, dlDeg);
    if (bestDl <= 1) s = Math.max(s, 92);
    else if (bestDl === 2) s += 6;

    return Math.min(100, Math.round(s));
  }

  function findBestCollectionMatch(searchTerm, collections){
    const v = variants(searchTerm);
    let best=null, bestScore=-1;

    for (const col of collections){
      let localBest=-1;
      for (const q of v){
        const s = scoreQueryToCollection(simplify(q), col);
        if (s>localBest) localBest=s;
        if (localBest>=100) break;
      }
      if (localBest>bestScore){
        bestScore=localBest; best=col;
      }
    }
    if (best && bestScore>=SCORE_THRESHOLD) return {best, bestScore};
    return {best:null, bestScore};
  }

  // ====== Intégration formulaire ======
  function enhance(){
    const forms = document.querySelectorAll('form[action="/search"]');
    if (!forms.length) return;

    forms.forEach(form=>{
      form.addEventListener('submit', async function(e){
        const input = form.querySelector('input[name="q"]');
        if (!input) return;

        const raw = input.value.trim();
        if (!raw) return;

        e.preventDefault();

        // 1) Mode "model code" => redirect produit seulement si matching confirmé
        if (looksLikeModelCode(raw)){
          const productUrl = await findProductUrlByModelCode(raw);
          if (productUrl){
            window.location.href = productUrl;
            return;
          }
          // sinon on continue sur collections / search standard
        }

        // 2) Collections (ta logique existante)
        const colUrl =
          `/search/suggest.json?q=${encodeURIComponent(raw)}&resources[type]=collection&resources[limit]=${SUGGEST_LIMIT}`;

        let collections = [];
        try{
          const r = await fetchWithTimeout(colUrl);
          if (r.ok){
            const data = await r.json();
            collections = (data?.resources?.results?.collections || [])
              .map(c => ({title:c.title, handle:c.handle, url:c.url}));
          }
        }catch(e){ /* ignore */ }

        const {best} = findBestCollectionMatch(raw, collections);
        if (best && best.url){
          window.location.href = best.url;
        } else {
          window.location.href = `/search?q=${encodeURIComponent(raw)}`;
        }
      }, {passive:false});
    });
  }

  if (document.readyState==='loading') document.addEventListener('DOMContentLoaded', enhance);
  else enhance();
})();
